import { createSlice } from "@reduxjs/toolkit";
import { RadioData } from "../types/radio";

const initialState = {
  activeURL: "",
  availableRadioEvents: [],
  activeEventSessions: {},
  selectedEvent: null,
  selectedSession: null,
  selectedType: null,
  selectedDriver: null,
  isPlaying: false,
  isWaveformLoading: false,
  skipForwardCount: 0,
  skipBackwardCount: 0,
  volume: 1,
  playbackSpeed: 1,
  zoom: 100,
  minZoom: 10,
  maxZoom: 200,
  queryPresignedURLSignal: 0,
} as RadioData;

export const radioSlice = createSlice({
  name: "radio",
  initialState,
  reducers: {
    setActiveURL: (state, action) => {
      state.activeURL = action.payload;
    },
    setAvailableRadioEvents: (state, action) => {
      state.availableRadioEvents = action.payload;
    },
    setActiveEventSessions: (state, action) => {
      const { sessions } = action.payload;
      const newSessions: Record<string, { raw: string[]; summary: string[] }> =
        {};
      sessions.forEach(
        (session: { session: string; raw: string[]; summary: string[] }) => {
          newSessions[session.session] = {
            raw: session.raw,
            summary: session.summary,
          };
        },
      );
      state.activeEventSessions = newSessions;
      if (state.selectedSession && !newSessions[state.selectedSession]) {
        state.selectedSession = null;
        state.selectedType = null;
        state.selectedDriver = null;
      } else if (state.selectedSession && state.selectedType) {
        const sessionData = newSessions[state.selectedSession];
        if (sessionData && state.selectedDriver) {
          const drivers =
            state.selectedType === "Raw"
              ? sessionData.raw
              : sessionData.summary;
          if (!drivers.includes(state.selectedDriver)) {
            state.selectedDriver = null;
          }
        }
      }
      // Signal to query presigned URL if all selections are valid, otherwise clear waveform
      if (
        state.selectedEvent &&
        state.selectedSession &&
        state.selectedType &&
        state.selectedDriver
      ) {
        state.queryPresignedURLSignal += 1;
      } else {
        state.activeURL = "";
      }
    },
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },
    setSelectedSession: (state, action) => {
      state.selectedSession = action.payload;
      if (state.selectedType && state.selectedDriver) {
        const sessionData = state.activeEventSessions[action.payload];
        if (sessionData) {
          const drivers =
            state.selectedType === "Raw"
              ? sessionData.raw
              : sessionData.summary;
          if (!drivers.includes(state.selectedDriver)) {
            state.selectedDriver = null;
          }
        }
      }
      // Signal to query presigned URL if all selections are valid, otherwise clear waveform
      if (
        state.selectedEvent &&
        state.selectedSession &&
        state.selectedType &&
        state.selectedDriver
      ) {
        state.queryPresignedURLSignal += 1;
      } else {
        state.activeURL = "";
      }
    },
    setSelectedType: (state, action) => {
      state.selectedType = action.payload;
      if (state.selectedSession && state.selectedDriver) {
        const sessionData = state.activeEventSessions[state.selectedSession];
        if (sessionData) {
          const drivers =
            action.payload === "Raw" ? sessionData.raw : sessionData.summary;
          if (!drivers.includes(state.selectedDriver)) {
            state.selectedDriver = null;
          }
        }
      }
      // Signal to query presigned URL if all selections are valid, otherwise clear waveform
      if (
        state.selectedEvent &&
        state.selectedSession &&
        state.selectedType &&
        state.selectedDriver
      ) {
        state.queryPresignedURLSignal += 1;
      } else {
        state.activeURL = "";
      }
    },
    setSelectedDriver: (state, action) => {
      state.selectedDriver = action.payload;
      // Signal to query presigned URL if all selections are valid, otherwise clear waveform
      if (
        state.selectedEvent &&
        state.selectedSession &&
        state.selectedType &&
        state.selectedDriver
      ) {
        state.queryPresignedURLSignal += 1;
      } else {
        state.activeURL = "";
      }
    },
    togglePlayPause: (state) => {
      state.isPlaying = !state.isPlaying;
    },
    setIsPlaying: (state, action) => {
      state.isPlaying = action.payload;
    },
    skipForward: (state) => {
      state.skipForwardCount += 1;
    },
    skipBackward: (state) => {
      state.skipBackwardCount += 1;
    },
    setWaveformLoading: (state, action) => {
      state.isWaveformLoading = action.payload;
    },
    setVolume: (state, action) => {
      state.volume = action.payload;
    },
    setPlaybackSpeed: (state, action) => {
      state.playbackSpeed = action.payload;
    },
    setZoom: (state, action) => {
      state.zoom = action.payload;
    },
    setMinZoom: (state, action) => {
      state.minZoom = action.payload;
    },
    setMaxZoom: (state, action) => {
      state.maxZoom = action.payload;
    },
  },
});

export const {
  setActiveURL,
  setAvailableRadioEvents,
  setActiveEventSessions,
  setSelectedEvent,
  setSelectedSession,
  setSelectedType,
  setSelectedDriver,
  togglePlayPause,
  setIsPlaying,
  skipForward,
  skipBackward,
  setWaveformLoading,
  setVolume,
  setPlaybackSpeed,
  setZoom,
  setMinZoom,
  setMaxZoom,
} = radioSlice.actions;
export default radioSlice.reducer;
